/**
 * Модуль «Апартаменты»: подписи, цвета, адреса площадок, форматирование.
 *
 * Одно место для всего, что иначе разъехалось бы по компонентам: «как
 * называется статус», «куда ведёт кнопка поддержки Booking», «как писать
 * сумму в злотых».
 */

import type { StayBookingStatus, StayPlatform, StayPropertyStatus } from '@tw/shared-types';

export const PLATFORM: Record<
  StayPlatform,
  {
    label: string;
    /** Фирменный цвет площадки — для точки и подложки значка, не для текста. */
    color: string;
    home: string;
    /** Переписка с хозяевами. */
    messages: string;
    /** Брони на самой площадке. */
    trips: string;
    support: string;
  }
> = {
  AIRBNB: {
    label: 'Airbnb',
    color: '#ff385c',
    home: 'https://www.airbnb.com/',
    messages: 'https://www.airbnb.com/guest/messages',
    trips: 'https://www.airbnb.com/trips/v1',
    support: 'https://www.airbnb.com/help',
  },
  BOOKING: {
    label: 'Booking',
    color: '#003b95',
    home: 'https://www.booking.com/',
    messages: 'https://secure.booking.com/mytrips.html',
    trips: 'https://secure.booking.com/mytrips.html',
    support: 'https://secure.booking.com/help.html',
  },
};

export const PROPERTY_STATUS: Record<
  StayPropertyStatus,
  { label: string; short: string; tone: string; dot: string }
> = {
  VERIFIED: {
    label: 'Проверенная',
    short: 'Проверена',
    tone: 'bg-success/12 text-success ring-1 ring-success/25',
    dot: 'bg-success',
  },
  NEUTRAL: {
    label: 'Без отметки',
    short: 'Без отметки',
    tone: 'bg-secondary text-soft ring-1 ring-border',
    dot: 'bg-muted-foreground/50',
  },
  BLACKLISTED: {
    label: 'Чёрный список',
    short: 'ЧС',
    tone: 'bg-destructive/12 text-destructive ring-1 ring-destructive/25',
    dot: 'bg-destructive',
  },
};

export const BOOKING_STATUS: Record<StayBookingStatus, string> = {
  CONFIRMED: 'Подтверждена',
  CANCELLED: 'Отменена',
};

/** Валюты в выпадающем списке: где компания бронирует чаще всего. */
export const CURRENCIES = ['EUR', 'PLN', 'USD', 'GBP', 'CZK', 'GEL', 'TRY', 'AED', 'RUB', 'UAH'];

export function formatMoney(minor: number | null, currency: string | null): string {
  if (minor === null || !currency) return '—';
  try {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency,
      maximumFractionDigits: minor % 100 === 0 ? 0 : 2,
    }).format(minor / 100);
  } catch {
    return `${(minor / 100).toFixed(2)} ${currency}`;
  }
}

const SHORT_DATE = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'short' });
const LONG_DATE = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'short', year: 'numeric' });

/** Календарная дата ГГГГ-ММ-ДД без сдвига на часовой пояс. */
function calendar(date: string): Date {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(y!, m! - 1, d!);
}

export function formatDate(date: string, withYear = false): string {
  return (withYear ? LONG_DATE : SHORT_DATE).format(calendar(date)).replace(' г.', '');
}

export function formatRange(checkIn: string, checkOut: string): string {
  const sameYear = checkIn.slice(0, 4) === checkOut.slice(0, 4);
  const thisYear = checkIn.slice(0, 4) === String(new Date().getFullYear());
  return `${formatDate(checkIn, !(sameYear && thisYear))} — ${formatDate(checkOut, !(sameYear && thisYear))}`;
}

export function plural(n: number, one: string, few: string, many: string): string {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
}

export const nightsLabel = (n: number): string => `${n} ${plural(n, 'ночь', 'ночи', 'ночей')}`;

export function nightsBetween(checkIn: string, checkOut: string): number {
  return Math.round((calendar(checkOut).getTime() - calendar(checkIn).getTime()) / 86_400_000);
}

export function todayIso(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** Где сейчас бронь относительно сегодняшнего дня. */
export function stayPhase(checkIn: string, checkOut: string): 'upcoming' | 'current' | 'past' {
  const today = todayIso();
  if (checkOut <= today) return 'past';
  if (checkIn > today) return 'upcoming';
  return 'current';
}

/** Площадка по адресу страницы — для кнопок захвата во встроенном браузере. */
export function platformOf(url: string): StayPlatform | null {
  try {
    const host = new URL(url).hostname;
    if (/(^|\.)airbnb\.(?:[a-z]{2,3}|co\.[a-z]{2}|com\.[a-z]{2})$/.test(host)) return 'AIRBNB';
    if (/(^|\.)booking\.com$/.test(host)) return 'BOOKING';
  } catch {
    // не адрес
  }
  return null;
}

/** Страница квартиры (а не поиск или главная) — её можно сохранить в базу. */
export function isListingUrl(url: string): boolean {
  try {
    const u = new URL(url);
    return (
      (platformOf(url) === 'AIRBNB' && /^\/rooms\/(?:plus\/)?\d+/.test(u.pathname)) ||
      (platformOf(url) === 'BOOKING' && /^\/hotel\/[a-z]{2}\/[^/]+\.html$/i.test(u.pathname))
    );
  } catch {
    return false;
  }
}

/**
 * Страница одной брони: подтверждение, детали поездки, чек. Открытие такой
 * страницы во встроенном браузере записывает бронь само.
 *
 * Зеркало isBookingPageUrl на сервере (apps/api/src/services/stay-capture.ts):
 * здесь — чтобы не слать снимок с каждой страницы, решает всё равно сервер.
 */
export function isBookingPageUrl(url: string): boolean {
  try {
    const path = new URL(url).pathname.toLowerCase();
    const platform = platformOf(url);
    if (platform === 'AIRBNB') {
      return /^\/trips\/v\d+\/[^/]+/.test(path) || /^\/reservation\/[a-z_-]+/.test(path) || /^\/book\/stays\/\d+\/confirm/.test(path);
    }
    if (platform === 'BOOKING') {
      return !/myreservations/.test(path) && /(confirmation|mybooking|booking[_-]details|\/mytrips\/[^/]+)/.test(path);
    }
  } catch {
    // неразбираемый адрес — не бронь
  }
  return false;
}

/**
 * Название квартиры из заголовка вкладки: площадки дописывают к нему своё
 * («… - Airbnb», «… – Booking.com»), а в базе это лишнее.
 */
export function cleanTitle(title: string): string {
  return title
    .replace(/\s*[-–|·]\s*(Airbnb|Booking\.com).*$/i, '')
    .replace(/^(Airbnb|Booking\.com)\s*[-–|·]\s*/i, '')
    .trim();
}

/** Подпись аккаунта в списках: площадка — только если её нет в названии. */
export function accountLabel(a: { platform: StayPlatform; title: string }): string {
  const name = PLATFORM[a.platform].label;
  return a.title.toLowerCase().includes(name.toLowerCase()) ? a.title : `${name} · ${a.title}`;
}

// ── Периоды таблицы броней ──────────────────────────────────────────────────

const iso = (d: Date): string => {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

export function addDaysIso(date: string, days: number): string {
  const d = calendar(date);
  d.setDate(d.getDate() + days);
  return iso(d);
}

/**
 * Неделя (с понедельника) или месяц со сдвигом от текущих: 0 — эта, -1 —
 * прошлая. `to` — день после конца периода: API берёт брони, у которых
 * заезд раньше `to`, а выезд позже `from`, то есть захватившие период
 * хотя бы одной ночью.
 */
export function periodRange(kind: 'week' | 'month', offset: number): { from: string; to: string; label: string } {
  const today = calendar(todayIso());
  if (kind === 'week') {
    const monday = new Date(today);
    monday.setDate(today.getDate() - ((today.getDay() + 6) % 7) + offset * 7);
    const from = iso(monday);
    const to = addDaysIso(from, 7);
    const label = `${formatRange(from, addDaysIso(from, 6))}${offset === 0 ? ' · эта неделя' : ''}`;
    return { from, to, label };
  }
  const first = new Date(today.getFullYear(), today.getMonth() + offset, 1);
  const next = new Date(first.getFullYear(), first.getMonth() + 1, 1);
  const name = new Intl.DateTimeFormat('ru-RU', { month: 'long', year: 'numeric' }).format(first).replace(' г.', '');
  return { from: iso(first), to: iso(next), label: name.charAt(0).toUpperCase() + name.slice(1) };
}
